import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "../../../../components";
import FormHeader from "../../../../components/form/FormHeader";
import SurveyDetailContentState from "../components/SurveyDetailContentState";
import SurveyDetailQuestions from "../components/SurveyDetailQuestions";
import SurveyDetailToolbar from "../components/SurveyDetailToolbar";
import SurveyInfoCards from "../components/SurveyInfoCards";
import { useGetSurveyDetail, usePublishSurveySubmission } from "../hooks";
import { useToast } from "../../../../components/toast-container";
import { useTranslation } from "react-i18next";
import UnpublishedSurveyModal from "../components/modals/UnpublishedSurveyModal";
import { goBackOrGoHome } from "../../../../utils/navigation";
import { SurveyStatus } from "../types/survey.types";

type SurveyDetailPageProps = {
  basePath?: string;
};

function SurveyDetailPage({ basePath = "/survey" }: SurveyDetailPageProps) {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { addToast } = useToast();
  const { survey, loading, error, refresh: refreshSurveyDetail } = useGetSurveyDetail(id);
  const { submitPublish, loading: publishing } = usePublishSurveySubmission();
  const [showUnpublishedModal, setShowUnpublishedModal] = useState(false);

  const handlePublishToggle = async () => {
    if (!survey) return;

    const isPublished = survey.status !== SurveyStatus.Unpublished;
    const result = await submitPublish(survey.id || "", isPublished);
    if (!result.success) return;

    await refreshSurveyDetail();
    addToast({
      type: "success",
      title: "Success!",
      message:
        result.response?.data.status !== SurveyStatus.Unpublished
          ? t("survey.published_successfully")
          : t("survey.unpublished_successfully"),
    });
  };
  const handleResponsesClick = () => {
    if (!survey) return;

    // Check if survey is unpublished
    if (survey.status === SurveyStatus.Unpublished) {
      setShowUnpublishedModal(true);
      return;
    }

    // Navigate to responses page if survey is published
    navigate(`${basePath}/${id}/responses`);
  };

  if (loading) {
    return <SurveyDetailContentState type="loading" />;
  }

  if (error || !survey) {
    return (
      <SurveyDetailContentState
        type="error"
        message={error || "Survey not found"}
        onBack={() => navigate(basePath)}
        onRetry={() => void refreshSurveyDetail()}
      />
    );
  }

  return (
    <div className="p-6">
      <FormHeader
        onBack={() => goBackOrGoHome(navigate)}
        title={survey.title}
        subtitle={survey.description ?? ""}
      />

      <Card className="p-6 dark:bg-gray-800 dark:border-gray-600">
        {/* Survey Info Cards */}
        <SurveyInfoCards survey={survey} />

        <SurveyDetailToolbar
          survey={survey}
          onEdit={() => navigate(`${basePath}/${id}/edit`)}
          onResponses={handleResponsesClick}
          onPublishToggle={handlePublishToggle}
          isPublishing={publishing}
        />

        <SurveyDetailQuestions questions={survey.questions} />
      </Card>

      <UnpublishedSurveyModal
        isOpen={showUnpublishedModal}
        onPublish={async () => {
          handlePublishToggle().then(() => {
            setShowUnpublishedModal(false);
          });
        }}
        onCancel={() => setShowUnpublishedModal(false)}
        isPublished={false}
      />
    </div>
  );
}

export default SurveyDetailPage;
